import store from '@/store/index'
import * as utils from '@/common/utils'
import ApiService from '@/common/api.service'
import { API_REPLACE_WARN_FIELD } from '@/store/api/postStatus.types'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    client_id: null,
    category_id: null,
    name: null,
    description: null,
    zone_shape: null,
    radius: null,
    stroke_color: null,
    stroke_opacity: null,
    stroke_weight: null,
    fill_color: '#d32f2f',
    fill_opacity: null,
    coordinates: [],
    sensors: {
      enter: { id: 'enter', enabled: false, unit_group_ids: [], vehicle_group_ids: [], code: 'ZONE:ENTER' },
      exit: { id: 'exit', enabled: false, unit_group_ids: [], vehicle_group_ids: [], code: 'ZONE:EXIT' },
      no_go: { id: 'no_go', enabled: false, unit_group_ids: [], vehicle_group_ids: [], code: 'ZONE:NO_GO' },
      forbidden_exit: { id: 'forbidden_exit', enabled: false, unit_group_ids: [], vehicle_group_ids: [], code: 'ZONE:FORBIDDEN_EXIT' },
      speeding: { id: 'speeding', enabled: false, unit_group_ids: [], vehicle_group_ids: [], max_speed: '80 kmh', code: 'ZONE:SPEEDING' },
      delayed_stop: { id: 'delayed_stop', enabled: false, unit_group_ids: [], vehicle_group_ids: [], duration: '60 minutes', code: 'ZONE:DELAYED_STOP' },
      forbidden_stop: { id: 'forbidden_stop', enabled: false, unit_group_ids: [], vehicle_group_ids: [], duration: '60 seconds', code: 'ZONE:FORBIDDEN_STOP' }
    },
    created_at: null,
    updated_at: null,

    queryMode: 'client_zones',
    zoneWidgetMode: null,
    stepNo: 1,
    totalSteps: 1,
    transparency: 50
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: null,
    updated_at: null
  }
}

function formDefaults () {
  return {
    zone_categories: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param zone Object
 *
 * @return Object
 */
function transformCreate (zone) {
  return {
    id: zone.id,
    client_id: zone.client_id,
    category_id: zone.category_id,
    name: zone.name,
    description: zone.description,
    zone_shape: zone.zone_shape,
    radius: zone.radius,
    stroke_color: zone.stroke_color,
    stroke_opacity: zone.stroke_opacity,
    stroke_weight: zone.stroke_weight,
    fill_color: zone.fill_color,
    fill_opacity: zone.fill_opacity,
    coordinates: zone.coordinates,
    sensors: zone.sensors
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param zone Object
 *
 * @return Object
 */
function transformUpdate (zone) {
  return {
    client_id: zone.client_id,
    category_id: zone.category_id,
    name: zone.name,
    description: zone.description,
    zone_shape: zone.zone_shape,
    radius: zone.radius,
    stroke_color: zone.stroke_color,
    stroke_opacity: zone.stroke_opacity,
    stroke_weight: zone.stroke_weight,
    fill_color: zone.fill_color,
    fill_opacity: zone.fill_opacity,
    coordinates: null,
    sensors: zone.sensors
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param name String
 * @param client_id int|null
 *
 * @return Object
 * @param client_id
 */
function transformCreateZoneCategory (name, client_id = null) {
  return {
    id: null,
    name: name,
    client_id: client_id
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param zone Object
 *
 * @return Object
 */
// function transformUpdate (zone) {
//   return {
//     id: zone.id,
//     client_id: zone.client_id,
//     zone_category_id: zone.category_id,
//     name: zone.name,
//     description: zone.description,
//     zone_shape: zone.zone_shape,
//     radius: zone.radius,
//     stroke_color: zone.stroke_color,
//     stroke_opacity: zone.stroke_opacity,
//     stroke_weight: zone.stroke_weight,
//     fill_color: zone.fill_color,
//     fill_opacity: zone.fill_opacity,
//     coordinates: zone.coordinates,
//     sensors: zone.sensors
//   }
// }

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`zones/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Fetches zone categories
   *
   * @param context
   * @param params
   */
  fetchZoneCategoriesListing (context, params) {
    const queryMode = context.getters['get'].queryMode
    if (queryMode === 'client_zones') {
      const clientId = context.getters['get'].client_id
      if (clientId !== null) {
        return ApiService
          .get(`geo/client/zones/categories/listing/by-client/${clientId}`, params)
          .then((response) => {
            context.commit(`setZoneCategories`, response.data.data)
            return response
          })
      } else {
        return new Promise((resolve) => {
          resolve()
        })
      }
    } else if (queryMode === 'global_zones') {
      return ApiService
        .get(`geo/global/zones/categories/listing`, params)
        .then((response) => {
          context.commit(`setZoneCategories`, response.data.data)
          return response
        })
    } else {
      return new Promise((resolve) => {
        context.commit(`setZoneCategories`, [])
        resolve()
      })
    }
  },

  /**
   * Deletes a zone category
   *
   * @param context
   * @param params
   */
  deleteZoneCategory (context, params) {
    const queryMode = context.getters['get'].queryMode
    const category_id = context.getters['get'].category_id
    if (queryMode === 'client_zones') {
      return ApiService.delete(`geo/client/zones/categories/${category_id}`).then((response) => {
        return response
      })
    } else if (queryMode === 'global_zones') {
      return ApiService.delete(`geo/global/zones/categories/${category_id}`).then((response) => {
        return response
      })
    } else {
      return new Promise((resolve) => {
        resolve()
      })
    }
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @param data
   * @return Promise
   */
  postZoneCategory (context, data) {
    const queryMode = context.getters['get'].queryMode
    if (queryMode === 'client_zones') {
      return ApiService
        .post(`geo/client/zones/categories`, transformCreateZoneCategory(data.data.name, data.data.client_id))
        .then((response) => {
          context.commit('setProp', { name: 'category_id', value: response.data.data.id })
          return response
        }).catch(() => {
          store.commit(API_REPLACE_WARN_FIELD, { warnFieldName: 'name', replaceWithFieldName: 'zone_category_name' })
        })
    } else if (queryMode === 'global_zones') {
      return ApiService
        .post(`geo/global/zones/categories`, transformCreateZoneCategory(data.data.name, data.data.client_id))
        .then((response) => {
          context.commit('setProp', { name: 'category_id', value: response.data.data.id })
          return response
        }).catch(() => {
          store.commit(API_REPLACE_WARN_FIELD, { warnFieldName: 'name', replaceWithFieldName: 'zone_category_name' })
        })
    } else {
      return new Promise((resolve) => {
        resolve()
      })
    }
  },

  post (context) {
    const url = context.getters['get'].queryMode === 'client_zones' ? `geo/client/zones` : `geo/global/zones`
    return ApiService
      .post(url, transformCreate(context.getters['get']))
      .then((response) => {
        context.commit('setProp', { name: 'id', value: response.data.data.id })
        return response
      })
  },

  put (context) {
    const id = context.getters['get'].id
    const url = context.getters['get'].queryMode === 'client_zones' ? `geo/client/zones/${id}` : `geo/global/zones/${id}`
    return ApiService
      .put(url, transformUpdate(context.getters['get']))
      .then((response) => {
        return response
      })
  },

  delete (context) {
    const id = context.getters['get'].id
    const url = context.getters['get'].queryMode === 'client_zones' ? `geo/client/zones/${id}` : `geo/global/zones/${id}`
    return ApiService
      .delete(url)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  setFormData (state, data) {
    state.form = {
      zone_categories: []
    }
  },

  setZoneCategories (state, zones) {
    state.form.zone_categories = zones
  },

  newZone (state) {
    state.data.name = null
    state.data.description = null
    state.data.sensors.enter.enabled = false
    state.data.sensors.exit.enabled = false
    state.data.sensors.no_go.enabled = false
    state.data.sensors.forbidden_exit.enabled = false
    state.data.sensors.speeding.enabled = false
    state.data.sensors.delayed_stop.enabled = false
    state.data.sensors.forbidden_stop.enabled = false
  },

  editZone (state, zone) {
    state.data.id = zone.id
    state.data.client_id = zone.client_id
    state.data.category_id = zone.category_id
    state.data.name = zone.name
    state.data.description = zone.description
    state.data.zone_shape = zone.zone_shape
    state.data.radius = zone.radius
    state.data.stroke_color = zone.stroke_color
    state.data.stroke_opacity = zone.stroke_opacity
    state.data.stroke_weight = zone.stroke_weight
    state.data.fill_color = zone.fill_color
    state.data.fill_opacity = zone.fill_opacity
    state.data.sensors = zone.sensors
    state.data.zoneWidgetMode = 'edit-zone'
    state.data.transparency = 100 - (zone.fill_opacity * 100)
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = Object.assign(defaults(), data)
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    utils.pullProp(state.data, name, index)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Sets the zone categories in form.
   *
   * @param state       Object
   * @param categories Array of objects in the form { id: 1, name: 'xyz' }
   */
  // setZoneCategories (state, categories) {
  //   state.form.zone_categories = categories
  // },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/zones/zonesModule.js